import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import { PesticideForm } from './-pesticide-form'
import type { Pesticide } from '@/lib/api/pesticides-api'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useCreatePesticide,
    useDeletePesticide,
    usePesticides,
    useUpdatePesticide,
} from '@/hooks/use-pesticides-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agriculture/pesticides',
)({
    component: PesticidesPage,
})

function PesticidesPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('pesticides.write')
    const [editing, setEditing] = useState<Pesticide | null>(null)
    const [open, setOpen] = useState(false)

    const { data } = usePesticides({ per_page: 100 })
    const rows = data?.data ?? []

    const createMut = useCreatePesticide()
    const updateMut = useUpdatePesticide(editing?.id ?? '')
    const deleteMut = useDeletePesticide()

    return (
        <div className="space-y-4 p-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">{m.pesticide_title()}</h1>
                {canWrite && (
                    <Button
                        size="sm"
                        onClick={() => {
                            setEditing(null)
                            setOpen(true)
                        }}
                    >
                        <Plus className="size-4" /> {m.pesticide_new()}
                    </Button>
                )}
            </div>

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead />
                        <TableHead>{m.pesticide_name()}</TableHead>
                        <TableHead>{m.pesticide_supplier()}</TableHead>
                        <TableHead>{m.pesticide_unit_price()}</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.id}>
                            <TableCell className="w-12">
                                {row.image_url && (
                                    <img
                                        src={row.image_url}
                                        alt={row.name}
                                        className="size-10 rounded object-cover"
                                    />
                                )}
                            </TableCell>
                            <TableCell className="font-medium">
                                {row.name}
                            </TableCell>
                            <TableCell>{row.supplier ?? '—'}</TableCell>
                            <TableCell>{row.unit_price ?? '—'}</TableCell>
                            <TableCell className="text-right space-x-1">
                                {canWrite && (
                                    <>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => {
                                                setEditing(row)
                                                setOpen(true)
                                            }}
                                        >
                                            <Pencil className="size-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => {
                                                if (
                                                    window.confirm(
                                                        m.pesticide_delete_confirm(),
                                                    )
                                                ) {
                                                    deleteMut.mutate(row.id)
                                                }
                                            }}
                                        >
                                            <Trash2 className="size-4" />
                                        </Button>
                                    </>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {editing ? m.pesticide_edit() : m.pesticide_new()}
                        </DialogTitle>
                    </DialogHeader>
                    <PesticideForm
                        initial={editing}
                        onSubmit={async (values) => {
                            try {
                                if (editing) {
                                    await updateMut.mutateAsync(values)
                                } else {
                                    await createMut.mutateAsync(values)
                                }
                                setOpen(false)
                            } catch {
                                // onError handler in the mutation hook surfaces the toast
                            }
                        }}
                        submitting={createMut.isPending || updateMut.isPending}
                    />
                </DialogContent>
            </Dialog>
        </div>
    )
}
